import { CSSProperties, MouseEvent, ReactNode, UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { BaseProps } from "../../BaseProps";
import * as Color from "../Color";
import { Batchable } from "../Batchable";
import { BoundsObserver } from "../../utilities/BoundsObserver";
import { EmptyArray } from "../../utilities/EmptyArray";
import { EmptyObject } from "../../utilities/EmptyObject";
import { debounce } from "../../utilities/debounce";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Checkbox, CheckboxValue } from "../../forms/Checkbox";
import { DataId } from "../DataId";
import { DataSource } from "../DataSource";
import { FilterMethods, Filterable } from "../Filterable";
import { Icon, IconName } from "../Icon";
import { SortMethods, Sortable } from "../Sortable";
import { Tag } from "../Tag";
import { Tooltip } from "../Tooltip";
import { Stack } from "../../layout";
import { createComponentStyleHook } from "../../tokens";
import { Italic } from "../../typography";
import classNames from "classnames";

import { AccessibilityProps } from "../../AccessibilityProps";
import { DisabledRowCheckHandler } from "./DisabledRowCheckHandler";
import { ExpandContractColumn } from "./ExpandContractColumn";
import { MessageContainer } from "./MessageContainer";
import { NoResultsOnFilter } from "./NoResultsOnFilter";
import { ResolvedColumn } from "./ResolvedColumn";
import { RowChildContentProvider } from "./RowChildContentProvider";
import { RowClickHandler } from "./RowClickHandler";
import { SelectedIdsChangeHandler } from "./SelectedIdsChangeHandler";
import { SelectionColumn } from "./SelectionColumn";
import { TableBody } from "./TableBody";
import { TableHeader } from "./TableHeader";
import { TableLayoutContext } from "./TableLayoutContext";
import { TableLoader } from "./TableLoader";
import { TableStylesContext } from "./TableStylesContext";
import { Actions, Custom, Date, Link, Tag as TagColumn, Text } from "./columnTypes";
import { Accounting } from "./columnTypes/Accounting";
import { Currency } from "./columnTypes/Currency";
import { Number } from "./columnTypes/Number";
import { SSN } from "./columnTypes/SocialSecurityNumber";
import { Telephone } from "./columnTypes/Telephone";
import { VirtualColumn } from "./columnTypes/VirtualColumn";
import { getColumns, resolveColumnKeys } from "./helpers";

import columnTypeTokenStyles from "./columnTypes/styles.module.css";
import tokenStyles from "./styles.module.css";

const useResolvedTableStyles = createComponentStyleHook<TableProps<object>>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "table",
});

const useResolvedColumnTypeStyles = createComponentStyleHook<TableProps<object>>({
  legacyStyles: () => import("./columnTypes/legacy/styles.module.scss"),
  tokenStyles: columnTypeTokenStyles,
});

/** The type of data source accepted by the `Table` component. */
export interface TableDataSource<TRow extends object>
  extends DataSource<TRow>, Filterable, Sortable, Batchable<FilterMethods & SortMethods> { }

/** The `Table` component properties. */
export interface TableProps<TRow extends object>
  extends BaseProps, Pick<AccessibilityProps, "aria-label" | "aria-labelledby" | "aria-describedby"> {
  /** The data source. */
  readonly dataSource: TableDataSource<TRow>;

  /** The table header. */
  readonly header?: ReactNode;

  /** The table footer. */
  readonly footer?: ReactNode;

  /** The set of selected row IDs. */
  readonly selectedIds?: ReadonlyArray<DataId>;

  /** Called when the set of selected row IDs are to be changed. If not specified row selection checkboxes will not display. */
  readonly onSelectedIdsChange?: SelectedIdsChangeHandler;

  /** Called when the row is to be tested if it should have a disabled checkbox */
  readonly onCheckDisabledRow?: DisabledRowCheckHandler<TRow>;

  /** The set of expanded row IDs. Only applies if `rowChildContent` is set. */
  readonly expandedIds?: ReadonlyArray<DataId>;

  /** Called when the set of expanded row IDs are to be changed. */
  readonly onExpandedIdsChange?: SelectedIdsChangeHandler;

  /** Called when a row is clicked. */
  readonly onRowClick?: RowClickHandler;

  /** Indicates whether table headers are hidden. If not specified defaults to false. */
  readonly hideHeaders?: boolean;

  /** Indicates optional child content to present for each row. */
  readonly rowChildContent?: RowChildContentProvider<TRow>;

  /** The table columns. */
  readonly children?: ReactNode;
}

/**
 * Gets the selection column for the specified table properties.
 * @param props The table properties.
 * @param selectedIdSet The set of selected IDs.
 * @returns The selection column instance, or null if no selection column needed.
 */
function useSelectionColumn<TRow extends object>(
  props: TableProps<TRow>,
  selectedIdSet: ReadonlySet<DataId>,
  styles: { readonly [key: string]: string },
) {
  const { dataSource, selectedIds = EmptyArray as ReadonlyArray<DataId>, testId, onCheckDisabledRow, onSelectedIdsChange } = props;
  const { rows } = dataSource;

  const selectAllChangeHandler = useCallback(
    (value: CheckboxValue) => {
      if (!value) {
        onSelectedIdsChange?.([], selectedIds);
      } else if (value) {
        const newSelectedIds = new Set(selectedIds);
        for (const row of rows) {
          if (!onCheckDisabledRow?.(row)) {
            newSelectedIds.add(row.id);
          }
        }

        onSelectedIdsChange?.(Array.from(newSelectedIds), selectedIds);
      }
    },
    [rows, selectedIds, onSelectedIdsChange, onCheckDisabledRow],
  );

  if (!onSelectedIdsChange) return null;

  let titleChecked: "indeterminate" | boolean = false;
  if (selectedIds.length > 0 && rows.every((row) => selectedIdSet.has(row.id) || onCheckDisabledRow?.(row))) titleChecked = true;
  else if (rows.some((row) => selectedIdSet.has(row.id) && !onCheckDisabledRow?.(row))) titleChecked = "indeterminate";

  return (
    <SelectionColumn
      name="$$selection"
      title={
        <>
          {rows.length > 0 && (
            <Tooltip
              content={
                titleChecked === true ? (
                  "Deselect all rows"
                ) : (
                  <>
                    Select all <Italic>visible</Italic> rows
                  </>
                )
              }
            >
              {(ref) => (
                <Checkbox
                  testId={`${testId}-select-all-checkbox`}
                  ref={ref}
                  value={titleChecked}
                  onChange={selectAllChangeHandler}
                  hideLabel
                  hideInstructions
                  aria-label={titleChecked ? "Deselect all rows" : "Select all rows"}
                />
              )}
            </Tooltip>
          )}
        </>
      }
      className={styles.SelectionCell}
      initialWidth={50}
      align="center"
      lock="left"
      sortable={false}
      selectedIds={selectedIds}
      selectedIdSet={selectedIdSet}
      onSelectedIdsChange={onSelectedIdsChange}
      onCheckDisabledRow={onCheckDisabledRow}
    />
  );
}

/** The expand/contract column element uses the ExpandContractHeader CSS class. */

/**
 * Gets the expand/contract column for the specified table properties.
 * @param props The table properties.
 * @param expandedIdSet The set of expanded IDs.
 * @returns The expand/contract column instance, or null if no expand/contract column needed.
 */
function useExpandContractColumn<TRow extends object>(
  props: TableProps<TRow>,
  expandedIdSet: ReadonlySet<DataId>,
  styles: { readonly [key: string]: string },
) {
  const { dataSource, expandedIds = EmptyArray as ReadonlyArray<DataId>, rowChildContent, onExpandedIdsChange } = props;
  const { rows } = dataSource;

  let titleExpanded: "indeterminate" | boolean = false;
  if (rows.every((row) => expandedIdSet.has(row.id))) titleExpanded = true;
  else if (rows.some((row) => expandedIdSet.has(row.id))) titleExpanded = "indeterminate";

  const expandAllChangeHandler = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      ev.preventDefault();
      if (titleExpanded === true) {
        onExpandedIdsChange?.([], expandedIds);
      } else {
        const newExpandedIds = new Set(expandedIds);
        for (const row of rows) {
          newExpandedIds.add(row.id);
        }

        onExpandedIdsChange?.(Array.from(newExpandedIds), expandedIds);
      }
    },
    [rows, titleExpanded, expandedIds, onExpandedIdsChange],
  );

  if (!rowChildContent) return null;

  let iconName: IconName;

  if (titleExpanded === "indeterminate") iconName = "remove";
  else if (titleExpanded) iconName = "chevron-down";
  else iconName = "chevron-right";

  return (
    <ExpandContractColumn
      name="$$expandContract"
      title={
        <>
          {rows.length > 0 && (
            <Tooltip
              content={
                titleExpanded === true ? (
                  "Contract all rows"
                ) : (
                  <>
                    Expand all <Italic>visible</Italic> rows
                  </>
                )
              }
            >
              {(ref) => (
                <button
                  className={styles.ExpandContractHeader}
                  ref={ref}
                  onClick={expandAllChangeHandler}
                  aria-expanded={titleExpanded !== "indeterminate" && titleExpanded}
                  aria-label={titleExpanded === true ? "Contract all rows" : "Expand all rows"}
                >
                  <Icon name={iconName} color={Color.Turqoise["500"]} />
                </button>
              )}
            </Tooltip>
          )}
        </>
      }
      initialWidth={50}
      align="center"
      lock="left"
      sortable={false}
      expandedIds={expandedIds}
      expandedIdSet={expandedIdSet}
      onExpandedIdsChange={onExpandedIdsChange}
    />
  );
}

/** A table component. */
export const Table = <TRow extends object>(props: TableProps<TRow>) => {
  const { mode, styles, StyleModeBoundary } = useResolvedTableStyles(props as unknown as TableProps<object>);
  const { styles: columnTypeStyles } = useResolvedColumnTypeStyles(props as unknown as TableProps<object>);

  const {
    header,
    footer,
    dataSource,
    selectedIds = EmptyArray as ReadonlyArray<DataId>,
    expandedIds = EmptyArray as ReadonlyArray<DataId>,
    children,
    rowChildContent,
    onRowClick,
    hideHeaders = false,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-describedby": ariaDescribedby,
  } = props;

  /*
    `isPending` is only true for the very first fetch. When the query key changes (filter, sort, page) the data source
    serves the previous result as placeholder data, so `isPending` is false while `rows` still answer the *old* query --
    `isPlaceholderData` is what identifies that window.
  */
  const { status = "idle", busy = false, rows, getFilter, isPending, isPlaceholderData = false } = dataSource;
  const isLoading = isPending || (busy && isPlaceholderData);
  /* Only blank the table on the very first fetch -- `keepPreviousData` exists so paging and sorting don't flash empty. */
  const displayRows = isPending ? [] : rows;

  const [columnWidths, setColumnWidths] = useState<Readonly<Record<string, number | "fit" | "span">>>({});
  const [computedColumnWidths, setComputedColumnWidths] = useState<Readonly<Record<string, number>>>({});
  const currentRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [showLeftIndicator, setShowLeftIndicator] = useState(false);
  const [showRightIndicator, setShowRightIndicator] = useState(false);
  const dataFetchCompleted = ["success", "error"].includes(status);
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Table,
      "table",
      {
        "has-data-load-indicators": true,
        "data-loaded": dataFetchCompleted,
        "data-loading": busy,
        initializing: status === "initializing",
        empty: rows.length === 0,
        "can-scroll-left": showLeftIndicator,
        "can-scroll-right": showRightIndicator,
      },
    ],
    testIdPrefix: "table",
  });

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const expandedIdSet = useMemo(() => new Set(expandedIds), [expandedIds]);

  let columnElements = getColumns(children, rows);

  // Additional internal columns to add based on selected behavior.
  const selectionColumn = useSelectionColumn(props, selectedIdSet, styles);
  const expandContractColumn = useExpandContractColumn(props, expandedIdSet, styles);
  if (expandContractColumn) columnElements = [expandContractColumn, ...columnElements];
  if (selectionColumn) columnElements = [selectionColumn, ...columnElements];

  /*
    We add a special column type if we have no locked left or right columns,
    because we house the shadow in the very last locked column on either side
    (easiest way to ensure it stays in table bounds)
  */
  if (!columnElements.some((column) => column.props.lock === "left")) {
    columnElements = [<VirtualColumn name="vcol-left-indicator" title="" lock="left" initialWidth={0} />, ...columnElements];
  }

  if (!columnElements.some((column) => column.props.lock === "right")) {
    columnElements = [...columnElements, <VirtualColumn name="vcol-right-indicator" title="" lock="right" initialWidth={0} />];
  }

  // Resolve a table-unique instance key for every column (distinct from the data `name`).
  const columns = resolveColumnKeys(columnElements);

  // Start organizing columns and stuff after we've sorted out the initial set of columns.
  const leftColumns: Array<ResolvedColumn> = [];
  const rightColumns: Array<ResolvedColumn> = [];
  const middleColumns: Array<ResolvedColumn> = [];
  const leftOffsets: Array<number> = [0];
  const rightOffsets: Array<number> = [0];

  for (const column of columns) {
    switch (column.element.props.lock) {
      case "left":
        leftColumns.push(column);
        break;
      case "right":
        rightColumns.push(column);
        break;
      default:
        middleColumns.push(column);
        break;
    }
  }

  const [boundsObserver] = useState(
    () =>
      new BoundsObserver((entries) => {
        let result = EmptyObject as Readonly<Record<string, number>>;

        for (const entry of entries) {
          if (entry.target === tableContainerRef.current) {
            setContainerWidth(entry.bounds.width);
            continue;
          }

          const { target, bounds } = entry;
          const { width } = bounds;

          const key = target.getAttribute("data-key");
          if (key === null) continue;

          result = { ...result, [key]: width };
        }

        setComputedColumnWidths((current) => {
          if (result === EmptyObject) return current;

          for (const key in result) {
            if (current[key] === result[key]) continue;
            return { ...current, ...result };
          }

          return current;
        });
      }),
  );

  useEffect(() => {
    return () => {
      boundsObserver.disconnect();
    };
  }, [boundsObserver]);

  useEffect(() => {
    setColumnWidths((current) => {
      for (const column of columns) {
        const { key } = column;
        const { initialWidth = "fit" } = column.element.props;

        if (current[key] === initialWidth) continue;

        if (current[key] === undefined) {
          current = { ...current, [key]: initialWidth };
        }
      }
      return current;
    });
  }, [columns]);

  const style = useMemo((): CSSProperties => {
    const style = columns.map((column) => {
      const { key } = column;
      const { initialWidth, minWidth } = column.element.props;

      const width = columnWidths[key] ?? initialWidth ?? "fit";

      let minValue = typeof width === "number" ? `${width}px` : "min-content";
      if (minWidth === "fit") minValue = "max-content";
      else if (minWidth !== undefined) minValue = `${minWidth}px`;

      if (width === "fit") {
        return `minmax(${minValue === "min-content" ? "max-content" : minValue}, max-content)`;
      }
      if (width === "span") return `minmax(${minValue}, 1fr)`;

      return `minmax(${minValue}, ${width}px)`;
    });

    style.push("minmax(0, auto)");

    return {
      gridTemplateColumns: style.join(" "),
      gridTemplateRows: rows.length === 0 ? "min-content" : `min-content repeat(${rows.length}, min-content)`,
    };
  }, [columns, columnWidths, rows.length]);

  const columnRefHandler = useCallback((key: string, ref: HTMLTableCellElement | null) => {
    const currentRef = currentRefs.current[key];
    if (ref === currentRef) return;

    if (currentRef && ref !== currentRef) {
      boundsObserver.unobserve(currentRef);
      currentRef.removeAttribute("data-key");
      currentRefs.current[key] = null;
    }

    if (ref) {
      currentRefs.current[key] = ref;
      ref.setAttribute("data-key", key);
      boundsObserver.observe(ref);
    }
  }, []);

  // Calculate the horizontal positions of each left locked column.
  for (let i = 1; i < leftColumns.length; i++) {
    const { key } = leftColumns[i - 1];
    const width = computedColumnWidths[key] ?? 0;
    leftOffsets.push(leftOffsets[leftOffsets.length - 1] + width);
  }

  // Calculate the horizontal positions of each right locked column.
  for (let i = rightColumns.length - 2; i >= 0; i--) {
    const { key } = rightColumns[i + 1];
    const width = computedColumnWidths[key] ?? 0;
    rightOffsets.push(rightOffsets[rightOffsets.length - 1] + width);
  }

  // Updates the scrollbar indicators based on the current scroll data, we set the styles directly so that we don't have to have another update loop take place (for perf).
  const updateScrollIndicator = useCallback(
    (left: number, right: number, max: number): void => {
      setShowLeftIndicator(left > 0);
      setShowRightIndicator(right < max);
    },
    [containerWidth],
  );

  const tableScrollHandler = useCallback(
    debounce((ev: UIEvent<HTMLTableElement>) => {
      const { target } = ev;
      const { scrollLeft, scrollWidth, offsetWidth } = target as HTMLTableElement;
      updateScrollIndicator(scrollLeft, scrollLeft + offsetWidth, scrollWidth - 1);
    }),
    [updateScrollIndicator],
  );

  useEffect(() => {
    if (!tableRef.current) return;

    const { scrollLeft, scrollWidth, offsetWidth } = tableRef.current as HTMLTableElement;
    updateScrollIndicator(scrollLeft, scrollLeft + offsetWidth, scrollWidth - 1);
  }, [containerWidth]);

  const tableContainerRefHandler = useCallback((ref: HTMLDivElement | null) => {
    if (tableContainerRef.current === ref) return;

    if (!ref && tableContainerRef.current) {
      boundsObserver.unobserve(tableContainerRef.current);
      tableContainerRef.current = null;
    }

    if (ref && !tableContainerRef.current) {
      boundsObserver.observe(ref);
      tableContainerRef.current = ref;
    }
  }, []);

  const hasFilters = useCallback(() => {
    return Object.keys(getFilter()).length > 0;
  }, [getFilter]);

  /* Keep the header and footer mounted while re-querying so the controls don't disappear and re-flow the table. */
  const hasNoFilteredResults = hasFilters() && rows.length === 0;
  const isHeaderFooterHidden = isPending || (!isLoading && hasNoFilteredResults);

  return (
    <StyleModeBoundary mode={mode}>
      <TableStylesContext.Provider value={{ styles, columnTypeStyles }}>
        <TableLayoutContext.Provider
          value={{ leftColumns, middleColumns, rightColumns, leftOffsets, rightOffsets, viewportWidth: containerWidth }}
        >
          <div {...baseHtmlProps}>
            <Stack spacing="none">
              {!!header && !isHeaderFooterHidden && <div className={classNames(styles.Header, "header")}>{header}</div>}
              <div ref={tableContainerRefHandler} className={classNames("table-container")}>
                <table
                  ref={tableRef}
                  className={classNames(styles.InnerTable, "inner-table")}
                  style={style}
                  aria-label={ariaLabel}
                  aria-labelledby={ariaLabelledby}
                  aria-describedby={ariaDescribedby}
                  aria-busy={isLoading}
                  aria-rowcount={rows.length}
                  onScroll={tableScrollHandler}
                >
                  {!hideHeaders && <TableHeader dataSource={dataSource} columnRef={columnRefHandler} />}
                  <TableBody rows={displayRows} expandedIdSet={expandedIdSet} rowChildContent={rowChildContent} onRowClick={onRowClick} />
                </table>
                {isLoading && displayRows.length === 0 && (
                  <div className={styles.LoadingContainer}>
                    <Stack alignment="center">
                      <TableLoader />
                    </Stack>
                  </div>
                )}
                {!isLoading && status === "success" && rows.length === 0 && (
                  <>
                    {hasFilters() ? (
                      <NoResultsOnFilter />
                    ) : (
                      <MessageContainer>
                        <Tag inverted>No items available</Tag>
                      </MessageContainer>
                    )}
                  </>
                )}
                {!isLoading && status === "error" && rows.length === 0 && (
                  <MessageContainer>
                    <Tag type="error" inverted>
                      Error loading items...
                    </Tag>
                  </MessageContainer>
                )}
              </div>
              {!!footer && !isHeaderFooterHidden && <div className={classNames(styles.Footer, "footer")}>{footer}</div>}
            </Stack>
          </div>
        </TableLayoutContext.Provider>
      </TableStylesContext.Provider>
    </StyleModeBoundary>
  );
};

/** A column which displays plain text. */
Table.Text = Text;

/** Column which displays a formatted date from either a Javascript Date object or a string which can be parsed into a date. */
Table.Date = Date;

/** Column which displays a value as one or more tags. */
Table.Tag = TagColumn;

/** Column which displays a series of actions. */
Table.Actions = Actions;

/** Column which displays a link using the value as the address. */
Table.Link = Link;

/** A column which displays custom content. */
Table.Custom = Custom;

/** A column which displays formatted numbers numerically. */
Table.Number = Number;

/** A column which displays formatted numbers numerically with currency symbol and negative numbers are colored in red. */
Table.Accounting = Accounting;

/** A column which displays formatted numbers numerically with currency symbol. */
Table.Currency = Currency;

/** A column which displays formatted numbers in a social security format. */
Table.SSN = SSN;

/** A column which displays formatted numbers numerically with currency symbol. */
Table.Telephone = Telephone;
